import React, { ReactNode, useMemo } from 'react';
import { FeedbacksList } from '@cp/entities/feedback';
import { Typography } from '@cp/ds/src/components/typography';
import { Box } from '@cp/ds/src/components/box';
import { Stack } from '@cp/ds/src/components/stack';
import { Tabs, TabsTokens } from '@cp/ds/src/components/tabs';
import { useTheme } from '@cp/ds/src/hooks';
import { ResumeAdditional, ResumeAuthorLocationLink, ResumeExperienceLabel, getResumeGeoPoint } from '@cp/entities/resume';
import { LocationUrlProvider } from '@cp/shared/location';
import { ClockIcon } from '@cp/ds/src/icons';
import { CatalogItemViewModel } from '@common/model/catalog/item/view';
import { useModalPromise } from '@cp/shared/modal';
import { CLIENT_CATALOG_REVIEW_PAGINATION_SIZE_MAX } from '@common/const/catalog';
import { coreCatalogItemApi } from '@cp/entities/catalogItem';
import { Tariffs } from 'src/entities/resume/ui/tariffs';
import { Schedule } from 'src/entities/resume/ui/schedule';
import { SafetyLabels } from 'src/entities/resume';
import { CatalogItemMap } from 'src/entities/catalog';
import { TRUSTWORTHINESS_MODAL } from '../lib/modals';

const buildGoogleMapsURL: LocationUrlProvider = (p) => new URL(`https://www.google.com/maps/?q=${p}`);

type ResumeTabsProps = {
  advantages?: ReactNode[] | ReactNode;
  data: CatalogItemViewModel;
};

export const ResumeTabs = ({ data, advantages }: ResumeTabsProps) => {
  const theme = useTheme();
  const checkTrustworthiness = useModalPromise(TRUSTWORTHINESS_MODAL);

  const { data: feedbacksData } = coreCatalogItemApi.endpoints.getFeedbacks.useQuery(
    { id: data?.id as number, pagination: CLIENT_CATALOG_REVIEW_PAGINATION_SIZE_MAX },
    { skip: !data?.id },
  );

  const map = useMemo(() => data && <CatalogItemMap location={getResumeGeoPoint(data)} sx={{ my: 1 }} />, [data]);

  const tabsData = [
    {
      id: 1,
      label: 'Обо мне',
      body: (
        <Stack gap={'24px'}>
          <Stack gap={'12px'}>
            <Typography component="p" sx={{ m: 0 }} variant="body">
              {data.translation?.description}
            </Typography>

            {data.experience && (
              <Box>
                <ResumeExperienceLabel before={<ClockIcon />} experience={data.experience} />
              </Box>
            )}
          </Stack>
          <Stack gap={'12px'}>
            <Typography component="span" variant="button bold">
              Адрес
            </Typography>
            <ResumeAuthorLocationLink data={data} provider={buildGoogleMapsURL} />
            {map}
          </Stack>
        </Stack>
      ),
    },
    {
      id: 2,
      label: 'Отзывы',
      body: <FeedbacksList hideTitle items={feedbacksData?.items} sx={{ backgroundColor: 'white', borderRadius: '16px', ml: '10px' }} />,
    },
    {
      id: 3,
      label: 'Оплата',
      body: (
        <Stack gap={'12px'}>
          {data.tariffs && <Tariffs data={data.tariffs} />}
          <ResumeAdditional data={data} />
        </Stack>
      ),
    },
    { id: 4, label: 'Расписание', body: <Schedule data={data.schedule} /> },
    {
      id: 5,
      label: 'Безопасность',
      body: <SafetyLabels isDocumentsChecked={data.clientProfile?.isDocumentsChecked} onClick={() => checkTrustworthiness({})} />,
    },
    { id: 6, label: 'Преимущества', body: <section>{advantages}</section> },
  ];

  return (
    <Box
      sx={{
        p: { xs: '20px 12px 10px', md: '20px 0 10px' },
        minHeight: { xs: '200px', md: 'auto' },
        height: { xs: '1000px', md: 'auto' },
        [TabsTokens.ActiveTextColor]: theme.palette['brand pink'],
      }}
    >
      <Tabs
        itemId={1}
        itemSx={{ px: { md: '16px' }, whiteSpace: 'nowrap', height: '35px' }}
        items={tabsData}
        sx={{
          width: 1,
          flexWrap: 'nowrap',
          overflow: 'auto',
          mb: '24px',
          height: '38px', //высота header контейнера, нужна чтобы не плавал контент внутри
          color: theme.palette['brand pink'],
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          msOverflowStyle: 'none',
        }}
      />
    </Box>
  );
};
